import type * as zero from "@rocicorp/zero"
import * as z from "zod/mini"
import type * as t from "../generated/schema"
import { assert } from "./assert"
import type { schema } from "./schema"

export const AuthData = z.object({
  id: z.string(),
  role: z.union([z.literal("crew"), z.literal("user")]),
})

export type AuthData = z.infer<typeof AuthData>

export async function assertChannelPermission(
  tx: zero.Transaction<schema, unknown>,
  authData: AuthData,
  channelId: string,
  permissionId: t.Row.channel_permission["permission_id"],
) {
  const channel = await tx.query.channel.where("id", channelId).one().run()
  assert(channel, "forbidden:channel")

  if (authData.role === "crew") return
  const repository = await tx.query.repository
    .where("id", channel.repository_id)
    .one()
    .run()
  if (authData.id === repository?.account_id) return

  // check everyone, channel, repository, organization scope, where previous scope overrides next scope
  // e.g. channel_member without "channel" scope wins over organization_member with "organization" scope
  const channelPermissions = await tx.query.channel_permission
    .where("channel_id", channelId)
    .where("permission_id", permissionId)
    .run()
  assert(channelPermissions.length, "forbidden:channel_permission")

  const everyoneScope = channelPermissions.find(
    (channelPermission) => channelPermission.scope === "everyone",
  )
  if (everyoneScope) return

  const channelScope = channelPermissions.find(
    (channelPermission) => channelPermission.scope === "channel",
  )
  const channelMember = await tx.query.channel_member
    .where("channel_id", channelId)
    .where("account_id", authData.id)
    .one()
    .run()
  if (channelScope && channelMember) return

  const repositoryScope = channelPermissions.find(
    (channelPermission) => channelPermission.scope === "repository",
  )
  const repositoryMember = await tx.query.repository_member
    .where("repository_id", channel.repository_id)
    .where("account_id", authData.id)
    .one()
    .run()
  if (repositoryScope && repositoryMember) {
    if (channelMember) throw new Error("forbidden:channel_member")
    return
  }

  const organizationScope = channelPermissions.find(
    (channelPermission) => channelPermission.scope === "organization",
  )
  const organizationMember = repository?.organization_id
    ? await tx.query.organization_member
        .where("organization_id", repository.organization_id)
        .where("account_id", authData.id)
        .one()
        .run()
    : undefined
  if (organizationScope && organizationMember) {
    if (channelMember) throw new Error("forbidden:channel_member")
    if (repositoryMember) throw new Error("forbidden:repository_member")
    return
  }

  throw new Error("forbidden:member")
}

export function assertIsLoggedIn(
  authData: AuthData | null | undefined,
): asserts authData {
  assert(authData, "unauthorized")
}
